import {
    AnyUpsellAddon,
    AvailableAddonType,
    Category,
    FerryAddonTypes,
    MyBookingSessionData,
    ServicePackageType,
    UpSellAddonBaggage,
    UpSellAddonFerry,
    UpSellAddonSeating,
    UpSellAddonServicePackage,
    UpSellAddonTravelInsurance,
    ValidationType,
} from './myBookingSession.interfaces';
import { fakeData as myBookingFakeData } from '@/api/MyBooking/myBooking.fake';
import {
    FerryProduct,
    FlightProduct,
    FlightType,
    ProductType,
} from '@/api/MyBooking/myBooking.interfaces';
import {
    FlightPax,
    GordianSeating,
    ProductType as CartProductType,
    SeatAvailability,
} from '@/api/Cart/cart.interfaces';
import { BaggageType } from '@shared/interfaces/baggage';
import { generateUUID } from '@shared/utils/uuid';
import { getRandomEnumValue } from '@shared/utils/enum';
import { random } from 'lodash';
import { getStoredFakeData } from '@/components/shared/DevPanel/DevPanel.helpers';
import { Market } from '@shared/config/market/market.models';
import { getCurrentMarketSpecs } from '@shared/config/market/market.helpers';

const stored = getStoredFakeData();
const market: Market = getCurrentMarketSpecs();
const Currency = market.moneyFormat.currency;

const flightCount = stored.products.filter((x) => x.type === 'Flight').length;
const ferryCount = stored.products.filter((x) => x.type === 'Ferry').length;

const bookingProducts = myBookingFakeData.products ?? [];
const flights = (
    bookingProducts.filter((p) => p.type === ProductType.Flight) as FlightProduct[]
).slice(0, flightCount);

const ferries = (
    bookingProducts.filter((p) => p.type === ProductType.Ferry) as FerryProduct[]
).slice(0, ferryCount);

const generateGordianSession = (): GordianSeating => {
    const randomSeatAvailability = getRandomEnumValue(SeatAvailability, [
        SeatAvailability.Unknown,
    ]);

    return {
        TripId: '18414a28-cad7-4289-8197-8859e4e025ba',
        TripAccessToken:
            'ZXlKaGJHY2lPaUpTVXpJMU5pSXNJblI1Y0NJNklrcFhWQ0o5LmV5SjBjbDlwWkNJNklqRTROREUwWVRJNExXTmhaRGN0TkRJNE9TMDRNVGszTFRnNE5UbGxOR1V3TWpWaVlTSXNJbWx1WDJsa0lqb2lNelUyWWpoalptSXRNV0l5WVMwME9HTXlMVGt6WXpBdFpHWTJaVEJtWXprME5qZGxJaXdpZG1Gc2FXUmZkVzUwYVd3aU9pSXlNREkwTFRBMExUQTBWREE1T2pJMk9qTTJMak0yTWprMU1sb2lmUS5CMHluLVp2YjlQMDdoUFFKQnE3TVRQc1FUdUUtTmlnZkxSWFNaU2hOV0lLd2o0aUhYOC1vSGdWRUNGN1YyajBBbXlRdW5EV015cnlVcWpFNVJyZmZIVUpLeWpMb3lhbEsyZXdHR2VRYkYtQ05uc09uWFJtXzVrNXRTaExteGdnT1h3cktlS1lRWE5JRl9sYzFURGVsc09lanVvSlpfaUhidkQ3MGpLZy1uTmFlZnMzckpQZ283QldwOXZCMF9NLWJXOWFjRGJrdmNlVlVlTjJHUFdjS1U1UzM0VGdJT2pVaHNLWERsRFptbTJSWXVHdG8zSXh2NzdLLW4yZEtHTlhDRzdTcGU5RlZSby05SzhCZGhDMXh0Z0Jjb242NkFyT1FabFA3MG8tdW1YS0k4OVhWUmY1RUlWRDBwTXZYejkwTVFrb3Vrd0Z1WWVNbGwwNDFib3k2bHc=',
        SearchId: 'a8196d1a-505d-474f-ab35-22becedb9239',
        SeatAvailability: randomSeatAvailability,
    };
};

const generatePaxList = () => {
    return [
        generatePax('Johannes', 'Gudmundsson'),
        generatePax('Maria', 'Gudmundsson'),
        generatePax('Ebbe', 'Gudmundsson', 8),
        generatePax('Olivia', 'Gudmundsson', 1),
    ];
};

const generatePax = (firstName: string, lastName: string, age = 99): FlightPax => ({
    AdditionalBaggage: {
        ShowWarning: true,
        SplitSector: true,
        Alternatives:
            age < 2
                ? []
                : [
                      {
                          // This should never return due to FE should filter this out since is Sector 1 and Sector 2
                          // but BE is saying SplitSector = true. Conflicts.
                          Id: generateUUID(),
                          Description: '',
                          Index: 0,
                          Pieces: 1,
                          IsSelected: false,
                          PerSector: [{ SectorNumber: 1 }, { SectorNumber: 2 }],
                          Weight: 8,
                          Type: BaggageType.CabinBag,
                          ShowDiscardPopUp: false,
                          Price: {
                              Amount: 199,
                              Currency,
                          },
                          PriceBreakdown: {
                              BaggagePrice: {
                                  Amount: 199,
                                  Currency,
                              },
                              TotalPrice: {
                                  Amount: 199,
                                  Currency,
                              },
                              BookingFee: {
                                  Amount: 0,
                                  Currency,
                              },
                          },
                      },
                      {
                          Id: generateUUID(),
                          Description: '',
                          Index: 0,
                          Pieces: 1,
                          IsSelected: false,
                          PerSector: [{ SectorNumber: 1 }],
                          Weight: 23,
                          Type: BaggageType.CheckedBag,
                          ShowDiscardPopUp: false,
                          Price: {
                              Amount: 795,
                              Currency,
                          },
                          PriceBreakdown: {
                              BaggagePrice: {
                                  Amount: 695,
                                  Currency,
                              },
                              BookingFee: {
                                  Amount: 100,
                                  Currency,
                              },
                              TotalPrice: {
                                  Amount: 795,
                                  Currency,
                              },
                          },
                      },
                      {
                          Id: generateUUID(),
                          Description: '',
                          Index: 0,
                          Pieces: 1,
                          IsSelected: false,
                          PerSector: [{ SectorNumber: 2 }],
                          Weight: 20,
                          Type: BaggageType.CheckedBag,
                          ShowDiscardPopUp: false,
                          Price: {
                              Amount: 595,
                              Currency,
                          },
                          PriceBreakdown: {
                              BaggagePrice: {
                                  Amount: 595,
                                  Currency,
                              },
                              TotalPrice: {
                                  Amount: 595,
                                  Currency,
                              },
                              BookingFee: {
                                  Amount: 0,
                                  Currency,
                              },
                          },
                      },
                      {
                          Id: generateUUID(),
                          Description: '',
                          Index: 0,
                          Pieces: 2,
                          IsSelected: false,
                          PerSector: [{ SectorNumber: 2 }],
                          Weight: 20,
                          Type: BaggageType.CheckedBag,
                          ShowDiscardPopUp: false,
                          Price: {
                              Amount: 1195,
                              Currency,
                          },
                          PriceBreakdown: {
                              BaggagePrice: {
                                  Amount: 1195,
                                  Currency,
                              },

                              TotalPrice: {
                                  Amount: 1195,
                                  Currency,
                              },
                              BookingFee: {
                                  Amount: 0,
                                  Currency,
                              },
                          },
                      },
                      {
                          Id: generateUUID(),
                          Description: '',
                          Index: 0,
                          Pieces: 3,
                          IsSelected: false,
                          PerSector: [{ SectorNumber: 2 }],
                          Weight: 20,
                          Type: BaggageType.CheckedBag,
                          ShowDiscardPopUp: false,
                          Price: {
                              Amount: 1495,
                              Currency,
                          },
                          PriceBreakdown: {
                              BaggagePrice: {
                                  Amount: 1495,
                                  Currency,
                              },

                              TotalPrice: {
                                  Amount: 1495,
                                  Currency,
                              },
                              BookingFee: {
                                  Amount: 0,
                                  Currency,
                              },
                          },
                      },
                  ],
    },
    PaxDescription: {
        IsAdult: age > 11,
        Age: age,
    },
    Nationality: null,
    Title: 0,
    Gender: 0,
    FirstName: firstName,
    LastName: lastName,
    SpecialNeedsOption: null,
    DateOfBirth: null,
    MandatoryFields: [],
});

const generateSeatingAddons = () => {
    return (flights || []).map((flight) => {
        if (flight.ticket.type === FlightType.Ryanair) {
            return;
        }

        return {
            Pnr: flight.ticket.pnr,
            Type: AvailableAddonType.Seating,
            ProductType: CartProductType.Flight,
            GordianSession: generateGordianSession(),
            Price: null,
            PriceOptions: null,
        } as UpSellAddonSeating;
    });
};

const generateBaggageAddons = () => {
    return (flights || []).map((flight) => {
        if (flight.ticket.type === FlightType.Ryanair) {
            return;
        }

        return {
            Pnr: flight.ticket.pnr,
            ProductType: CartProductType.Flight,
            Type: AvailableAddonType.Baggage,
            Pax: generatePaxList(),
            Price: null,
            PriceOptions: null,
        } as UpSellAddonBaggage;
    });
};

const generateFerryAddonBase = (
    type: FerryAddonTypes,
    productId: number
): Partial<UpSellAddonFerry> => {
    const maxValue = 2;

    return {
        ProductId: productId,
        IsCancellable: false,
        Id: generateUUID(),
        InfoMessages: [],
        SupplierReference: generateUUID(),
        Value: 0,
        ProductType: CartProductType.Ferry,
        Type: type,
        PriceOptions: null,
        Validations: [
            {
                Value: maxValue,
                ValidationType: ValidationType.Maximum,
                Message: `Det finns bara ${maxValue} tillgängliga`,
            },
        ],
        Price: {
            Amount: 0,
            Currency,
        },
    };
};

const generateFerryAddon = (
    type: FerryAddonTypes,
    productId: number,
    index: number
): UpSellAddonFerry => {
    const addon = {
        ...generateFerryAddonBase(type, productId),
    };

    switch (type) {
        case AvailableAddonType.Seat:
            return {
                ...addon,
                Title: ['Vilstol', 'Vilstol (avskilt rum)'][index % 2],
                Price: {
                    Amount: random(100, 200),
                    Currency,
                },
                Worth: {
                    Value: 1,
                    Description: 'Max en passagerare',
                },
            } as UpSellAddonFerry;
        case AvailableAddonType.Meal:
            return {
                ...addon,
                Title:
                    [
                        'Julbord all inclusive, vuxen',
                        'Julbord all inclusive, barn',
                        'Julbord all inclusive, spädbarn',
                    ][index] ?? 'Julbord leftovers',
                Price: {
                    Amount: [100, 200, 0][index] ?? 0,
                    Currency,
                },
            } as UpSellAddonFerry;
        case AvailableAddonType.Pet:
            return {
                ...addon,
                Category: Category.Kennel,
                Title: [
                    'Husdjur ombord - Hund ombord, på anvisad plats',
                    'Husdjur ombord - Hund ombord i bil',
                ][index % 2],
            } as UpSellAddonFerry;
        case AvailableAddonType.Lounge:
            return {
                ...addon,
                Category: Category.Lounge,
                Price: {
                    Amount: random(500, 1000),
                    Currency,
                },
                Title: 'Pluslounge - Plus Lounge, avskild lounge inkl snacks Fr 18 år',
            } as UpSellAddonFerry;
        default:
            return {
                ...addon,
                Title: `DEFAULT - add fake data for ${type}`,
            } as UpSellAddonFerry;
    }
};

const generateFerryAddonsByType = (
    type: FerryAddonTypes,
    productId: number,
    count: number
): UpSellAddonFerry[] => {
    return Array.from({ length: count }).map((_, index) =>
        generateFerryAddon(type, productId, index)
    );
};

const generateFerryAddons = () => {
    return ferries.flatMap((ferry) => [
        ...generateFerryAddonsByType(AvailableAddonType.Seat, ferry.productId, 2),
        ...generateFerryAddonsByType(AvailableAddonType.Cabin, ferry.productId, 4),
        ...generateFerryAddonsByType(AvailableAddonType.Meal, ferry.productId, 4),
        ...generateFerryAddonsByType(AvailableAddonType.Pet, ferry.productId, 2),
        ...generateFerryAddonsByType(AvailableAddonType.Lounge, ferry.productId, 1),
    ]);
};

const generateTravelInsuranceAddon = () => {
    return {
        Type: AvailableAddonType.TravelInsurance,
        ProductType: CartProductType.Other,
        Coverage: 'Europe',
        Price: { Amount: 399, Currency },
        PriceOptions: null,
        IsSelected: false,
    } as UpSellAddonTravelInsurance;
};

const generateServicePackageAddons = (): UpSellAddonServicePackage[] => {
    const addons: UpSellAddonServicePackage[] = [];
    if (!stored.bookingSession?.hasServicePackage) {
        return [];
    }

    if (stored.bookingSession?.hasServicePackageBasic) {
        addons.push({
            PackageType: ServicePackageType.Basic,
            Price: {
                Amount: 299,
                Currency,
            },
            ProductType: CartProductType.Other,
            Type: AvailableAddonType.ServicePackage,
            PriceOptions: null,
        });
    }
    if (stored.bookingSession?.hasServicePackagePremium) {
        addons.push({
            PackageType: ServicePackageType.Premium,
            Price: {
                Amount: 549,
                Currency,
            },
            ProductType: CartProductType.Other,
            Type: AvailableAddonType.ServicePackage,
            PriceOptions: null,
        });
    }
    return addons;
};

export const fakeData: MyBookingSessionData = {
    AvailableAddons: [
        ...generateSeatingAddons(),
        ...generateBaggageAddons(),
        ...generateFerryAddons(),
        ...generateServicePackageAddons(),
        generateTravelInsuranceAddon(),
    ].filter((x) => x) as AnyUpsellAddon[],
};
